/**
 * Compare Profiles — side-by-side iron vs iron.
 */

import { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { ChevronLeft, Swords, Lock } from 'lucide-react-native';
import { fetchCompareProfiles, type CompareProfile, type LiftEntry } from '@/lib/profileCompare';

const GOLD = '#B8972E';

function StatRow({
  label,
  leftVal,
  rightVal,
  c,
}: {
  label: string;
  leftVal: string;
  rightVal: string;
  c: any;
}) {
  return (
    <View style={styles.statRow}>
      <Text style={[styles.statVal, { color: c.text }]}>{leftVal}</Text>
      <Text style={[styles.statLabel, { color: c.textTertiary }]}>{label}</Text>
      <Text style={[styles.statVal, { color: c.text }]}>{rightVal}</Text>
    </View>
  );
}

function LiftBreakdownRow({
  left,
  right,
  c,
}: {
  left: LiftEntry;
  right: LiftEntry;
  c: any;
}) {
  const lWins = left.rawKg > right.rawKg;
  const rWins = right.rawKg > left.rawKg;

  return (
    <View style={styles.breakdownRow}>
      <View style={[
        styles.breakdownCell,
        lWins && { backgroundColor: GOLD + '22' },
      ]}>
        <Text style={[styles.breakdownVal, { color: lWins ? GOLD : c.text }]}>
          {left.weight > 0 ? `${left.weight} lb` : '0 lb'}
        </Text>
      </View>
      <Text style={[styles.breakdownLabel, { color: c.textTertiary }]}>{left.lift}</Text>
      <View style={[
        styles.breakdownCell,
        rWins && { backgroundColor: GOLD + '22' },
      ]}>
        <Text style={[styles.breakdownVal, { color: rWins ? GOLD : c.text }]}>
          {right.weight > 0 ? `${right.weight} lb` : '0 lb'}
        </Text>
      </View>
    </View>
  );
}

export default function CompareScreen() {
  const { userId1, userId2 } = useLocalSearchParams<{ userId1: string; userId2: string }>();
  const { profile: currentProfile } = useAuth();
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { isProPlus } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [left, setLeft] = useState<CompareProfile | null>(null);
  const [right, setRight] = useState<CompareProfile | null>(null);

  const leftId = userId1 || currentProfile?.id;
  const rightId = userId2;

  useEffect(() => {
    if (!leftId || !rightId) return;
    setLoading(true);
    fetchCompareProfiles(leftId, rightId).then(({ left: l, right: r }) => {
      setLeft(l);
      setRight(r);
      setLoading(false);
    });
  }, [leftId, rightId]);

  const fmt = (v: number | null | undefined, fallback = '—') =>
    v != null && isFinite(v) ? String(Math.round(v)) : fallback;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      <Stack.Screen options={{ headerShown: false }} />

      <View style={[styles.navBar, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
          <ChevronLeft size={22} color={c.textSecondary} />
        </TouchableOpacity>
        <View style={styles.navCenter}>
          <Swords size={16} color={c.accent} />
          <Text style={[styles.navTitle, { color: c.text }]}>COMPARE</Text>
        </View>
        <View style={{ width: 22 }} />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={c.accent} />
        </View>
      ) : !left || !right ? (
        <View style={styles.center}>
          <Text style={{ color: c.textSecondary }}>Could not load profiles</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          {/* Avatars */}
          <View style={styles.avatarRow}>
            <View style={styles.avatarCol}>
              {left.avatar_url ? (
                <Image source={{ uri: left.avatar_url }} style={styles.avatar} />
              ) : (
                <View style={[styles.avatarFallback, { backgroundColor: c.surfaceVariant }]}>
                  <Text style={[styles.avatarInit, { color: c.text }]}>
                    {(left.display_name || left.handle || '?')[0].toUpperCase()}
                  </Text>
                </View>
              )}
              <Text style={[styles.name, { color: c.text }]} numberOfLines={1}>
                {left.display_name || left.handle || 'Unknown'}
              </Text>
            </View>

            <View style={[styles.vsBadge, { backgroundColor: c.accent + '20' }]}>
              <Text style={[styles.vsText, { color: c.accent }]}>VS</Text>
            </View>

            <View style={styles.avatarCol}>
              {right.avatar_url ? (
                <Image source={{ uri: right.avatar_url }} style={styles.avatar} />
              ) : (
                <View style={[styles.avatarFallback, { backgroundColor: c.surfaceVariant }]}>
                  <Text style={[styles.avatarInit, { color: c.text }]}>
                    {(right.display_name || right.handle || '?')[0].toUpperCase()}
                  </Text>
                </View>
              )}
              <Text style={[styles.name, { color: c.text }]} numberOfLines={1}>
                {right.display_name || right.handle || 'Unknown'}
              </Text>
            </View>
          </View>

          {/* Stats comparison */}
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <StatRow
              label="Strength"
              leftVal={fmt(left.duel_standard_strength_kg)}
              rightVal={fmt(right.duel_standard_strength_kg)}
              c={c}
            />
            <View style={[styles.divider, { backgroundColor: c.border }]} />
            <StatRow
              label="Rank"
              leftVal={left.current_rank ? `#${left.current_rank}` : '—'}
              rightVal={right.current_rank ? `#${right.current_rank}` : '—'}
              c={c}
            />
            <View style={[styles.divider, { backgroundColor: c.border }]} />
            <StatRow
              label="Division"
              leftVal={left.division || '—'}
              rightVal={right.division || '—'}
              c={c}
            />
            <View style={[styles.divider, { backgroundColor: c.border }]} />
            <StatRow
              label="Workouts"
              leftVal={String(left.totalWorkouts)}
              rightVal={String(right.totalWorkouts)}
              c={c}
            />
            <View style={[styles.divider, { backgroundColor: c.border }]} />
            <StatRow
              label="Win Record"
              leftVal={left.duelStats ? `${left.duelStats.wins}W-${left.duelStats.losses}L` : '—'}
              rightVal={right.duelStats ? `${right.duelStats.wins}W-${right.duelStats.losses}L` : '—'}
              c={c}
            />
            <View style={[styles.divider, { backgroundColor: c.border }]} />
            <StatRow
              label="Streak"
              leftVal={`${left.streak}d`}
              rightVal={`${right.streak}d`}
              c={c}
            />
          </View>

          {/* Top lifts */}
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.cardTitle, { color: c.textTertiary }]}>TOP LIFTS</Text>
            {left.topLifts.map((lLift, i) => {
              const rLift = right.topLifts[i];
              return (
                <View key={lLift.liftKey}>
                  {i > 0 && <View style={[styles.divider, { backgroundColor: c.border }]} />}
                  <StatRow
                    label={lLift.lift}
                    leftVal={lLift.weight > 0 ? `${lLift.weight} lbs` : '—'}
                    rightVal={rLift && rLift.weight > 0 ? `${rLift.weight} lbs` : '—'}
                    c={c}
                  />
                </View>
              );
            })}
          </View>

          {/* Strength Breakdown (Pro) */}
          {isProPlus ? (
            <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={[styles.cardTitle, { color: c.textTertiary }]}>STRENGTH BREAKDOWN</Text>
              {left.allExercises.map((lEx, i) => {
                const rEx = right.allExercises[i] ?? { ...lEx, weight: 0, rawKg: 0 };
                return (
                  <View key={lEx.liftKey}>
                    {i > 0 && <View style={[styles.divider, { backgroundColor: c.border }]} />}
                    <LiftBreakdownRow left={lEx} right={rEx} c={c} />
                  </View>
                );
              })}
            </View>
          ) : (
            <View style={[styles.card, styles.lockedCard, { backgroundColor: c.surface, borderColor: GOLD + '44' }]}>
              <Lock size={20} color={GOLD} />
              <Text style={[styles.lockedTitle, { color: c.text }]}>FULL BREAKDOWN</Text>
              <Text style={[styles.lockedSubtitle, { color: c.textSecondary }]}>
                Upgrade to Pro to see complete lift-by-lift comparison
              </Text>
              <TouchableOpacity
                style={[styles.upgradeBtn, { backgroundColor: GOLD }]}
                onPress={() => router.push('/paywall')}
              >
                <Text style={styles.upgradeBtnText}>UPGRADE</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  navBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  navCenter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  navTitle: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 1,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  avatarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 20,
  },
  avatarCol: {
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 4,
  },
  avatarFallback: {
    width: 64,
    height: 64,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInit: {
    fontSize: 24,
    fontWeight: '900',
  },
  name: {
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  vsBadge: {
    width: 36,
    height: 36,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vsText: {
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 1,
  },
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 14,
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 10,
    textAlign: 'center',
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  statVal: {
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  statLabel: {
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
    width: 80,
    letterSpacing: 0.3,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
  },
  // Breakdown
  breakdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  breakdownCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 6,
    borderRadius: 2,
  },
  breakdownVal: {
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  breakdownLabel: {
    fontSize: 10,
    fontWeight: '600',
    textAlign: 'center',
    width: 80,
    letterSpacing: 0.3,
  },
  // Locked card
  lockedCard: {
    alignItems: 'center',
    gap: 8,
    paddingVertical: 24,
  },
  lockedTitle: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 1,
  },
  lockedSubtitle: {
    fontSize: 12,
    textAlign: 'center',
    maxWidth: 240,
    lineHeight: 18,
  },
  upgradeBtn: {
    marginTop: 4,
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 2,
  },
  upgradeBtnText: {
    color: '#000',
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 1,
  },
});
